import { View } from "ol";
import Feature from "ol/Feature";
import GeoJSON from "ol/format/GeoJSON";
import { Geometry } from "ol/geom";
import Draw from 'ol/interaction/Draw.js';
import VectorLayer from "ol/layer/Vector";
import Map from 'ol/Map';
import { Pixel } from "ol/pixel";
import VectorSource from "ol/source/Vector";
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useOutletContext, useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import setTitle from "../../../components/common/SetTitle";
import { fetchCSRFToken } from "../../../components/common/utilities/api";
import LoadingSpinner from "../../../components/loadingSpinner/LoadingSpinner";
import { OutletIProps } from "../../../components/sitebase/Base.types";
import ModalWidget from "../../map/components/widgets/ModalWidget";
import { MapPresenter } from "../../map/MapPresenter";
import { epsg3857 } from "../../map/maputils";
import { MapComponent } from "../../map/MapV3";
import { InlineRule } from "./Inline.types";
import { areaOfInterestToFeatures, featuresToAreaOfInterest } from "./inlineGeo";
import { IMPACT_LEVELS } from "./inlineImpact";
import { INLINE_MENU_LINKS } from "./InlineMenu";
import { useInlineDatasets } from "./useInlineDatasets";

import './InlineRules.css';
import styles from './InlineRuleForm.module.css';

/** The two INLINE products a rule can be built on. This choice only decides which
 *  leadtimes are offered - the API stores the leadtime in `layer_name`. */
type InlineProduct = "INLINE0" | "INLINE2";

const PRODUCT_OPTIONS: { id: string, value: InlineProduct, label: string }[] = [
    { id: "layer-0", value: "INLINE0", label: "River Flash Flood Impact" },
    { id: "layer-1", value: "INLINE2", label: "Urban Flash Flood Hazard" },
];

/** Leadtime choices, the `layer_name` each one is stored as, and the products it
 *  is offered for. */
const LEADTIME_OPTIONS: { id: string, label: string, layerName: string | null, products: InlineProduct[] }[] = [
    { id: "leadtime-0-6", label: "0-6H", layerName: "INLINE1", products: ["INLINE0", "INLINE2"] },
    { id: "leadtime-7-24", label: "7-24H", layerName: "INLINE3", products: ["INLINE0"] },
    { id: "leadtime-7-48", label: "7-48H", layerName: "INLINE2", products: ["INLINE0"] },
    { id: "leadtime-25-48", label: "25-48H", layerName: "INLINE4", products: ["INLINE0"] },
    { id: "leadtime-49-120", label: "49-120H", layerName: null, products: ["INLINE0"] },
];

const IMPACT_ATTRIBUTE_NAME = "impact_p90";
const DEFAULT_IMPACT_LEVEL = "High";

/** Labels of the predefined exposure thresholds. These are not submitted yet. */
const PREDEFINED_THRESHOLD_LABELS = [
    "Total population affected",
    "Education facilities affected",
    "Health facilities affected",
    "Energy generation facilities affected",
];

const GROUPING_LEVELS = [0, 1, 2, 3];
const DEFAULT_GROUPING_LEVEL = 2;

/** Outlines only the NUTS regions of one level, leaving the fill clickable. */
const nutsStyle = (level: number) => [{
    filter: ['==', ['get', 'LEVL_CODE'], level],
    style: {
        'stroke-color': 'blue',
        'stroke-width': 2,
        'fill-color': 'rgba(255, 255, 255, 0.0)',
    }
}];

/** Preset email frequencies, in hours - the unit `notification_email_frequency` is stored in. */
const FREQUENCY_OPTIONS: { id: string, label: string, hours: number }[] = [
    { id: "rule-frequency-0", label: "Hourly with each new forecast", hours: 1 },
    { id: "rule-frequency-1", label: "Every 6 hours", hours: 6 },
    { id: "rule-frequency-2", label: "Every 12 hours", hours: 12 },
    { id: "rule-frequency-3", label: "Every day", hours: 24 },
];
const CUSTOM_FREQUENCY_ID = "rule-frequency-4";
const DEFAULT_FREQUENCY_HOURS = 1;

/** The forecasts shown in the "what the system will do" example table. */
const FREQUENCY_PREVIEW_ROWS = [
    ...Array.from({ length: 13 }, (_unused, offset) => ({ label: `${String((12 + offset) % 24).padStart(2, "0")}:00`, offset })),
    { label: "... (next day) 12:00", offset: 24 },
];

const EMPTY_THRESHOLDS = {
    low: { start: "", end: "" },
    medium: { start: "", end: "" },
    high: { start: "", end: "" },
};

const toInputValue = (value: number | null | undefined) => (value === null || value === undefined ? "" : String(value));

const parseFloatOrNull = (value: string): number | null => {
    const trimmed = value.trim();
    if (trimmed === "") {
        return null;
    }
    const parsed = parseFloat(trimmed);
    return Number.isNaN(parsed) ? null : parsed;
};

const errorMessage = (data: unknown) => {
    const error = data as { message?: string; detail?: string } | null;
    return error?.message || error?.detail || JSON.stringify(data) || "Unknown error";
};

/**
 * Create or edit an INLINE rule. Mounted on both `rules/new` and `rules/:ruleId/edit`;
 * with a `ruleId` in the route it loads that rule and prefills every field, and saves
 * with a PATCH instead of a POST.
 */
export function InlineRuleForm() {
    const { ruleId } = useParams();
    const isEdit = Boolean(ruleId);
    const navigate = useNavigate();
    const context = useOutletContext<OutletIProps>();
    const { setExtraMenuLinks } = context;

    const [loading, setLoading] = useState<boolean>(isEdit);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [ruleName, setRuleName] = useState<string>("");
    const [product, setProduct] = useState<InlineProduct>("INLINE0");
    const [leadtime, setLeadtime] = useState<string | null>(null);
    const [minimumImpactLevel, setMinimumImpactLevel] = useState<string>(DEFAULT_IMPACT_LEVEL);
    const [groupingLevel, setGroupingLevel] = useState<number | null>(DEFAULT_GROUPING_LEVEL);
    const [frequencyId, setFrequencyId] = useState<string>(FREQUENCY_OPTIONS[0].id);
    const [customFrequencyHours, setCustomFrequencyHours] = useState<string>("");
    const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
    const [isUploadingRoi, setIsUploadingRoi] = useState<boolean>(false);
    const [uploadedRegionFile, setUploadedRegionFile] = useState<string>("");
    const [exposureTab, setExposureTab] = useState<"predefined" | "user">("predefined");
    const [datasetName, setDatasetName] = useState<string>("");
    const [datasetDescription, setDatasetDescription] = useState<string>("");
    const [datasetType, setDatasetType] = useState<string>("exposure");
    const [thresholds, setThresholds] = useState<typeof EMPTY_THRESHOLDS>(EMPTY_THRESHOLDS);
    const {
        uploadedDataset,
        setUploadedDataset,
        availableDatasets,
        isUploadingDataset,
        fetchAvailableDatasets,
        uploadDataset,
    } = useInlineDatasets();

    const roiFile = useRef<HTMLInputElement>(null);
    const datasetFile = useRef<HTMLInputElement>(null);
    /** The dataset whose raster range has already been turned into default thresholds,
     *  so that neither a status poll nor an edit prefill overwrites what is in the form. */
    const autoFilledThresholdsFor = useRef<number | null>(null);

    const [featureSource] = useState<VectorSource>(() => new VectorSource({ wrapX: true }));
    const [draw] = useState<Draw>(() =>
        new Draw({
            source: featureSource,
            type: 'Polygon',
        })
    );
    const [featureOverlay] = useState<VectorLayer>(() => new VectorLayer({
        source: featureSource,
        style: {
            'stroke-color': 'red',
            'stroke-width': 4,
            'fill-color': 'rgba(0, 0, 0, 0.15)',
        },
    }));
    const [nutsOverlay] = useState<VectorLayer>(() => new VectorLayer({
        source: new VectorSource({
            url: '/api/fms/nuts/',
            format: new GeoJSON(),
        }),
        style: nutsStyle(DEFAULT_GROUPING_LEVEL),
        visible: false
    }));
    const [map] = useState<Map>(() => new Map({
        layers: [
            nutsOverlay,
            featureOverlay
        ],
        view: new View({
            center: epsg3857({ lat: 50, lon: 15 }),
            zoom: 4,
        }),
        controls: [],
    }));
    const [presenter] = useState<MapPresenter>(() => new MapPresenter(map));

    const frequencyHours = frequencyId === CUSTOM_FREQUENCY_ID
        ? (parseInt(customFrequencyHours, 10) || 0)
        : (FREQUENCY_OPTIONS.find((option) => option.id === frequencyId)?.hours ?? 0);

    const refreshSelectedRegions = useCallback(() => {
        setSelectedRegions(featureSource.getFeatures()
            .filter((feature) => feature.get("NUTS_ID"))
            .map((feature) => `${feature.get("LEVL_CODE")}-${feature.get("NAME_LATN")}`));
    }, [featureSource]);

    const singleclick = useCallback((evt: { dragging: boolean; pixel: Pixel; }) => {
        if (evt.dragging) {
            return;
        }
        map.forEachFeatureAtPixel(evt.pixel, function (feature) {
            if (featureSource.hasFeature(feature as Feature<Geometry>)) {
                featureSource.removeFeature(feature as Feature<Geometry>);
            } else {
                featureSource.addFeature(feature as Feature<Geometry>);
            }
            refreshSelectedRegions();
        });
    }, [refreshSelectedRegions, featureSource, map]);

    const selectProduct = (value: InlineProduct) => {
        setProduct(value);
        setLeadtime((previous) => {
            const option = LEADTIME_OPTIONS.find((candidate) => candidate.id === previous);
            return option && option.products.includes(value) ? previous : null;
        });
    };

    const uploadRegionFile = useCallback(() => {
        const file = roiFile.current?.files?.[0];
        if (!file) {
            toast.error("Please select a region of interest file to upload.");
            return;
        }

        const formData = new FormData();
        formData.append("file", file);
        setIsUploadingRoi(true);
        fetch('/api/fms/upload-edera-region', {
            method: "POST",
            body: formData
        }).then((response) => {
            if (response.ok) {
                return response.json().then((data) => {
                    setIsUploadingRoi(false);
                    toast.info(`${data.message}`);
                    featureSource.clear();
                    featureSource.addFeatures(new GeoJSON().readFeatures(data.file_data));
                    setUploadedRegionFile(data.file_path);
                    refreshSelectedRegions();
                });
            } else if (response.status === 413) {
                setIsUploadingRoi(false);
                toast.error("File is too large. The file limit is 25MB");
            } else {
                response.json().then((data) => {
                    console.error("Error uploading file:", data.message);
                    setIsUploadingRoi(false);
                    toast.error("Error uploading file: " + (data?.message || "Unknown error"));
                });
            }
        }).catch((error) => {
            console.error("Error uploading file:", error);
            setIsUploadingRoi(false);
            toast.error("Error uploading file: " + (error instanceof Error ? error.message : String(error)));
        });
    }, [refreshSelectedRegions, featureSource]);

    const applyRule = useCallback((rule: InlineRule) => {
        setRuleName(rule.name);
        // The product radio only picks which leadtimes are offered - the API stores the
        // leadtime in layer_name, so an existing rule always comes back as the river product.
        setProduct("INLINE0");
        setLeadtime(LEADTIME_OPTIONS.find((option) => option.layerName === rule.layer_name)?.id ?? null);

        const impactAttribute = rule.attributes?.find((attribute) => attribute.attribute_name === IMPACT_ATTRIBUTE_NAME);
        const impactValue = impactAttribute ? parseInt(impactAttribute.attribute_value, 10) : NaN;
        setMinimumImpactLevel(IMPACT_LEVELS.find((level) => level.value === impactValue)?.name ?? DEFAULT_IMPACT_LEVEL);

        const grouping = rule.notification_grouping === null || rule.notification_grouping === undefined
            ? NaN
            : parseInt(rule.notification_grouping, 10);
        setGroupingLevel(Number.isNaN(grouping) ? null : grouping);

        const hours = rule.notification_email_frequency ?? DEFAULT_FREQUENCY_HOURS;
        const preset = FREQUENCY_OPTIONS.find((option) => option.hours === hours);
        setFrequencyId(preset ? preset.id : CUSTOM_FREQUENCY_ID);
        setCustomFrequencyHours(preset ? "" : String(hours));

        setUploadedRegionFile(rule.file_of_interest ?? "");
        featureSource.clear();
        const features = areaOfInterestToFeatures(rule.area_of_interest);
        if (features.length > 0) {
            featureSource.addFeatures(features);
            const extent = featureSource.getExtent();
            if (extent && extent.every(Number.isFinite)) {
                map.getView().fit(extent, { padding: [30, 30, 30, 30], maxZoom: 10 });
            }
        }
        refreshSelectedRegions();

        setThresholds({
            low: { start: toInputValue(rule.threshold_low_start), end: toInputValue(rule.threshold_low_end) },
            medium: { start: toInputValue(rule.threshold_medium_start), end: toInputValue(rule.threshold_medium_end) },
            high: { start: toInputValue(rule.threshold_high_start), end: toInputValue(rule.threshold_high_end) },
        });

        const dataset = rule.datasets?.[0];
        if (dataset) {
            setExposureTab("user");
            autoFilledThresholdsFor.current = dataset.id;
            setUploadedDataset({
                id: dataset.id,
                name: dataset.name,
                type: dataset.type ?? "exposure",
                status: dataset.status,
                status_message: dataset.status_message ?? dataset.status_detail ?? "",
                raster_min: dataset.raster_min,
                raster_max: dataset.raster_max,
            });
        }
    }, [refreshSelectedRegions, setUploadedDataset, featureSource, map]);

    useEffect(() => {
        if (!ruleId) {
            return;
        }

        setLoading(true);
        const headers: Record<string, string> = {};
        if (context.user.token) {
            headers["Authorization"] = `Token ${context.user.token}`;
        }

        fetch(`/api/inline/rules/${ruleId}/`, { method: "GET", headers, credentials: "include" })
            .then(async (response) => {
                const data = await response.json().catch(() => null);
                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}: ${errorMessage(data)}`);
                }
                return data as InlineRule;
            })
            .then((rule) => applyRule(rule))
            .catch((error) => {
                console.error("Failed to load rule:", error);
                toast.error(`Failed to load rule: ${error instanceof Error ? error.message : String(error)}`);
            })
            .finally(() => setLoading(false));
    }, [ruleId, context.user.token, applyRule]);

    useEffect(() => {
        if (exposureTab === "user") {
            fetchAvailableDatasets();
        }
    }, [exposureTab, fetchAvailableDatasets]);

    useEffect(() => {
        if (!uploadedDataset || uploadedDataset.status !== "done") {
            return;
        }
        if (uploadedDataset.raster_min === null || uploadedDataset.raster_max === null) {
            return;
        }
        if (autoFilledThresholdsFor.current === uploadedDataset.id) {
            return;
        }

        autoFilledThresholdsFor.current = uploadedDataset.id;
        setThresholds({
            low: { start: String(uploadedDataset.raster_min), end: "" },
            medium: { start: "", end: "" },
            high: { start: String(0.9 * uploadedDataset.raster_max), end: String(uploadedDataset.raster_max) },
        });
    }, [uploadedDataset]);

    useEffect(() => {
        setExtraMenuLinks(INLINE_MENU_LINKS, "Rules");
        map.addInteraction(draw);
    }, [setExtraMenuLinks, draw, map]);

    useEffect(() => {
        setTitle(isEdit ? "INLINE Edit Rule" : "INLINE");
    }, [isEdit]);

    const saveRule = async () => {
        const name = ruleName.trim();
        if (!name) {
            toast.error("Rule name is required.");
            return;
        }
        if (name.length > 255) {
            toast.error("Rule name must be 255 characters or fewer.");
            return;
        }

        if (!leadtime) {
            toast.error("Please select at least one leadtime.");
            return;
        }

        const layer_name = LEADTIME_OPTIONS.find((option) => option.id === leadtime)?.layerName;
        if (!layer_name) {
            toast.error("Select a supported leadtime: 0-6H, 7-24H, 25-48H, or 7-48H.");
            return;
        }

        const ruleData = {
            name,
            layer_name,
            area_of_interest: featuresToAreaOfInterest(featureSource.getFeatures()),
            file_of_interest: uploadedRegionFile || null,
            notification_grouping: groupingLevel === null ? null : String(groupingLevel),
            grouping_attribute: "NUTS_NAME",
            notification_email_frequency: frequencyHours || DEFAULT_FREQUENCY_HOURS,
            datasets: uploadedDataset ? [uploadedDataset.id] : [],
            threshold_low_start: parseFloatOrNull(thresholds.low.start),
            threshold_low_end: parseFloatOrNull(thresholds.low.end),
            threshold_medium_start: parseFloatOrNull(thresholds.medium.start),
            threshold_medium_end: parseFloatOrNull(thresholds.medium.end),
            threshold_high_start: parseFloatOrNull(thresholds.high.start),
            threshold_high_end: parseFloatOrNull(thresholds.high.end),
            attributes: [{
                attribute_name: IMPACT_ATTRIBUTE_NAME,
                attribute_operation: ">=",
                attribute_value: String(IMPACT_LEVELS.find((level) => level.name === minimumImpactLevel)?.value ?? 3),
            }],
            ...(isEdit ? {} : { owner: context.user.user_id }),
        };

        setIsSaving(true);
        try {
            const headers: Record<string, string> = { "Content-Type": "application/json" };
            if (context.user.token) {
                headers["Authorization"] = `Token ${context.user.token}`;
                headers["X-CSRFToken"] = await fetchCSRFToken();
            }

            const response = await fetch(isEdit ? `/api/inline/rules/${ruleId}/` : `/api/inline/rules/`, {
                method: isEdit ? "PATCH" : "POST",
                headers,
                credentials: "include",
                body: JSON.stringify(ruleData),
            });

            const data = await response.json().catch(() => null);
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${errorMessage(data)}`);
            }

            toast.info(`Rule ${isEdit ? "updated" : "created"} successfully: ${data?.name ?? name}`);
            setTimeout(() => navigate(isEdit ? `/inline/rules/${ruleId}` : "/inline/rules/"), 1000);
        } catch (error) {
            console.error(`Error ${isEdit ? "updating" : "creating"} rule:`, error);
            toast.error(`Error ${isEdit ? "updating" : "creating"} rule: ${error instanceof Error ? error.message : String(error)}`);
        } finally {
            setIsSaving(false);
        }
    };

    if (loading) {
        return <div className="py-4"><LoadingSpinner /></div>;
    }

    return <div className="container py-3 pb-5">
        <ToastContainer position="top-center" />
        <form className="form">
            <div className="d-flex flex-column">
                <label htmlFor="name" className="form-label efas-heading-border-bottom mb-3">Rule Name</label>
                <input id="name" name="name" type="text" className="form-control" placeholder="Something to identify by..."
                    value={ruleName} onChange={(e) => setRuleName(e.target.value)} />
            </div>
            {/* TODO load supported layers from api */}

            <div className="d-flex flex-column mt-2">
                <label className="form-label efas-heading-border-bottom mb-3">Layer</label>
                <div className="d-flex flex-column ">
                    {PRODUCT_OPTIONS.map((option) => <div className="form-check mb-3" key={option.id}>
                        <input type="radio" className="form-check-input" id={option.id} name="layer" value={option.value}
                            checked={product === option.value} onChange={() => selectProduct(option.value)} />
                        <label className="form-check-label" htmlFor={option.id}>{option.label}</label>
                    </div>)}
                </div>

                <div className="d-flex flex-column">
                    <label htmlFor="impact" className="form-label efas-heading-border-bottom mb-3">Leadtime</label>

                    {LEADTIME_OPTIONS.filter((option) => option.products.includes(product)).map((option) =>
                        <div className="form-check mb-3" key={option.id}>
                            <input type="radio" className="form-check-input" id={option.id} name="leadtime" value={option.id}
                                checked={leadtime === option.id} onChange={() => setLeadtime(option.id)} />
                            <label className="form-check-label" htmlFor={option.id}>{option.label}</label>
                        </div>)}
                </div>
            </div>

            <div className="d-flex flex-column">
                <label className="form-label efas-heading-border-bottom mb-3">Exposure</label>
                <ul className="nav nav-tabs mb-3">
                    <li className="nav-item">
                        <button type="button" className={`nav-link form-label efas-heading-border ${exposureTab === "predefined" ? "active" : ""}`} onClick={() => setExposureTab("predefined")}>Predefined Exposure</button>
                    </li>
                    <li className="nav-item">
                        <button type="button" className={`nav-link form-label efas-heading-border ${exposureTab === "user" ? "active" : ""}`} onClick={() => setExposureTab("user")}>User Provided Exposure</button>
                    </li>
                </ul>
            </div>
            <div className="mb-3">
                {exposureTab === "predefined" && <div>
                    <div className="d-flex flex-column py-2 pb-4">
                        <div className="d-flex flex-column">
                            <label htmlFor="alertlevel" className="form-label mb-3">Minimum impact level - selected <strong>{minimumImpactLevel}</strong></label>

                            <div className="btn-group" role="group">
                                {IMPACT_LEVELS.map((level, index) => <Fragment key={level.name}>
                                    <input type="radio" className={`btn-check edera-label-${level.name.toLowerCase()} edera-button`} name="alertlevel" id={`alert${index}`}
                                        checked={minimumImpactLevel === level.name} onChange={() => setMinimumImpactLevel(level.name)} />
                                    <label className={`btn edera-label-${level.name.toLowerCase()}`} htmlFor={`alert${index}`}>{level.name}</label>
                                </Fragment>)}
                            </div>
                        </div>
                    </div>
                    <div className="d-flex flex-column py-2 pb-4">
                        <label htmlFor="predefined-thresholds" className="form-label efas-heading-border-bottom mb-3">Optional thesholds <button className="btn btn-warning my-1 py-0" type="button" data-bs-toggle="collapse" data-bs-target=".multi-collapse" aria-expanded="false" aria-controls="multiCollapseExample1 multiCollapseExample2">Click to toggle</button></label>

                        <div className="collapse multi-collapse fs-8" id="multiCollapseExample1">
                            <div id="predefined-thresholds" role="group">
                                {PREDEFINED_THRESHOLD_LABELS.map((label, index) => <div className="input-group mb-2" key={label}>
                                    <label className={`btn border fs-7 ${styles.thresholdLabel}`} htmlFor={`predefined-threshold-${index}`}>{label}</label>
                                    <select id={`predefined-threshold-op-${index}`} name="op" className={`form-select ${styles.thresholdOpSelect}`} defaultValue="gt">
                                        <option value="lte">&lt;=</option>
                                        <option value="lt">&lt;</option>
                                        <option value="eq">=</option>
                                        <option value="gt">&gt;</option>
                                        <option value="gte">&gt;=</option>
                                    </select>
                                    <input type="number" name={`predefined-threshold-${index}`} id={`predefined-threshold-${index}`} autoComplete="off" className="form-control" />
                                </div>)}
                            </div>
                        </div>
                    </div>
                </div>}

                {exposureTab === "user" && <div>
                    <p className="fw-lighter fst-italic">Upload your own exposure dataset as a GeoTIFF (.tif/.tiff).</p>
                    <div className="mb-3">
                        <label htmlFor="dataset-available" className="form-label">Select from already available</label>
                        <select className="form-select" id="dataset-available" disabled={availableDatasets.length === 0} value={uploadedDataset ? uploadedDataset.id : ""} onChange={(e) => {
                            const id = parseInt(e.target.value, 10);
                            setUploadedDataset(availableDatasets.find((dataset) => dataset.id === id) ?? null);
                        }}>
                            <option value="">{availableDatasets.length === 0 ? "-- No datasets available, upload a new one below --" : "-- Upload a new dataset --"}</option>
                            {availableDatasets.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                        </select>
                    </div>
                    {uploadedDataset && <div className="alert alert-success py-2">
                        Uploaded dataset: <strong>{uploadedDataset.name}</strong>
                    </div>}
                    {uploadedDataset && uploadedDataset.status === "processing" && <div className="alert alert-info py-2 d-flex align-items-center gap-2">
                        <div className="d-flex flex-column justify-content-center align-items-center mx-auto text-center"><LoadingSpinner /> {uploadedDataset.status_message}</div>
                    </div>}
                    {uploadedDataset && uploadedDataset.status === "done" && <div className="alert alert-success py-2">
                        {uploadedDataset.status_message}
                    </div>}
                    {uploadedDataset && uploadedDataset.status === "failed" && <div className="alert alert-danger py-2">
                        {uploadedDataset.status_message}
                    </div>}
                    <div className="mb-2">
                        <label htmlFor="dataset-file" className="form-label">File</label>
                        <input ref={datasetFile} type="file" className="form-control" id="dataset-file" name="dataset-file" accept=".tif,.tiff" onChange={(e) => {
                            const selected = e.target.files && e.target.files[0];
                            if (selected) {
                                setDatasetName(selected.name.replace(/\.(tif|tiff)$/i, ""));
                            }
                        }} />
                    </div>
                    <div className="mb-2">
                        <label htmlFor="dataset-name" className="form-label">Name</label>
                        <input type="text" className="form-control" id="dataset-name" name="dataset-name" value={datasetName} onChange={(e) => setDatasetName(e.target.value)} placeholder="Dataset name" />
                    </div>
                    <div className="mb-2">
                        <label htmlFor="dataset-description" className="form-label">Description <span className="fw-lighter">(optional)</span></label>
                        <textarea className="form-control" id="dataset-description" name="dataset-description" value={datasetDescription} onChange={(e) => setDatasetDescription(e.target.value)} placeholder="Describe this dataset" />
                    </div>
                    <div className="mb-2">
                        <label htmlFor="dataset-type" className="form-label">Type</label>
                        <select className="form-select" id="dataset-type" name="dataset-type" value={datasetType} onChange={(e) => setDatasetType(e.target.value)}>
                            <option value="exposure">Exposure</option>
                        </select>
                    </div>
                    {!isUploadingDataset && <div className="input-group">
                        <button className="btn btn-outline-dark" type="button" onClick={() => uploadDataset(datasetFile.current?.files?.[0], datasetName, datasetDescription, datasetType)}>Upload</button>
                    </div>
                        || <h4 className="text-center">Uploading ...<LoadingSpinner /></h4>}
                    {uploadedDataset && uploadedDataset.status === "done" && uploadedDataset.raster_min !== null && uploadedDataset.raster_max !== null && <div className="d-flex flex-column mt-4">
                        <label className="form-label efas-heading-border-bottom mb-3">Thresholds</label>
                        <p className="fw-lighter fst-italic mb-2">Raster pixel values range from <strong>{uploadedDataset.raster_min}</strong> to <strong>{uploadedDataset.raster_max}</strong>.</p>
                        <div className="d-flex fw-bold mb-1 gap-2">
                            <div className={styles.thresholdCol}>Threshold level</div>
                            <div className={styles.thresholdCol}>Start range</div>
                            <div className={styles.thresholdCol}>End range</div>
                        </div>
                        {(["low", "medium", "high"] as const).map((level) => <div key={level} className="d-flex align-items-center gap-2 mb-2">
                            <div className={`text-capitalize rounded px-2 py-1 edera-label-${level} ${styles.thresholdCol}`}>{level}</div>
                            <input type="number" className={`form-control ${styles.thresholdCol}`} placeholder="Start" value={thresholds[level].start}
                                onChange={(e) => setThresholds((prev) => ({ ...prev, [level]: { ...prev[level], start: e.target.value } }))} />
                            <input type="number" className={`form-control ${styles.thresholdCol}`} placeholder="End" value={thresholds[level].end}
                                onChange={(e) => setThresholds((prev) => ({ ...prev, [level]: { ...prev[level], end: e.target.value } }))} />
                        </div>)}
                    </div>}
                </div>}
            </div>
            <div className="d-flex flex-column">
                <div className="efas-heading-border-bottom mb-3">Region of interest</div>
                <div className="mb-3">
                    {uploadedRegionFile && <div>The currently uploaded region file: <strong className="mx-2">{uploadedRegionFile.split("/").pop()}</strong></div>}
                    {!uploadedRegionFile && <ModalWidget toggleButton={<div role="button" className="btn btn-outline-dark">Upload a region of interest file</div>} title="Upload a region of interest file" className="mb-2">
                        {!isUploadingRoi && <div className="input-group">
                            <input ref={roiFile} type="file" className="form-control" id="roi-file" name="roi-file" accept=".zip,.geojson" />
                            <button className="btn btn-outline-dark" type="button" onClick={uploadRegionFile}>Upload</button>
                        </div>
                            || <h4 className="text-center">Uploading ...<LoadingSpinner /></h4>}

                        <div className="mt-4">
                            <p>Uploading a file will <strong>reset</strong> your manual region selection.</p>
                            <p>Once uploaded your file will be <strong>reprojected to EPSG 3857</strong> (if it's not already), and shown on the map below.</p>
                            Supported formats:
                            <ul>
                                <li>.zip file containing a valid Shapefile - including the shp, shx, dbf</li>
                                <li>.geojson file</li>
                            </ul>
                            Only the geometry of Polygon features will be used, all other attributes will be ignored.
                            The feature intersection is calcuated using <a target="_blank" href="https://docs.djangoproject.com/en/5.2/ref/contrib/gis/geos/#django.contrib.gis.geos.GEOSGeometry.intersects">django.contrib.gis.geos.GEOSGeometry.intersects</a>
                        </div>

                    </ModalWidget>}
                </div>

                <div id="roi" className="btn-group" role="group">
                    <input type="radio" className="btn-check" name="roi" id="roi-freehand" autoComplete="off" defaultChecked={true} onClick={() => {
                        nutsOverlay.setVisible(false);
                        map.un('singleclick', singleclick);
                        map.addInteraction(draw);
                    }} />
                    <label className="btn btn-outline-dark" htmlFor="roi-freehand">Freehand</label>
                    {GROUPING_LEVELS.map((level) => <Fragment key={level}>
                        <input type="radio" className="btn-check" name="roi" id={`roi-nuts-${level}`} autoComplete="off" onClick={() => {
                            nutsOverlay.setVisible(true);
                            map.on('singleclick', singleclick);
                            map.removeInteraction(draw);
                            nutsOverlay.setStyle(nutsStyle(level));
                        }} />
                        <label className="btn btn-outline-dark" htmlFor={`roi-nuts-${level}`}>NUTS Level {level}</label>
                    </Fragment>)}
                    <input className="btn-check" name="btnradio" id="roi-clear" autoComplete="off" onClick={() => {
                        setUploadedRegionFile("");
                        featureSource.clear();
                        setSelectedRegions([]);
                    }} />
                    <label className="btn btn-outline-danger" htmlFor="roi-clear">Clear</label>
                </div>
            </div>
            <div className='d-flex flex-column'>
                <div>
                    <MapComponent presenter={presenter} height="25vh" />
                </div>
                <div className="mx-auto text-center fs-6">
                    Selected regions
                    <div id="selected">
                        {selectedRegions.length > 0
                            ? selectedRegions.map((region) => <div key={region}>{region}</div>)
                            : "No NUTS regions selected"}
                    </div>
                </div>
            </div>

            <div className="d-flex flex-column my-4">
                <label className="form-label efas-heading-border-bottom mb-3">Grouping <button className="btn btn-warning my-1 py-0" type="button" data-bs-toggle="collapse" data-bs-target="#grouping-collapse" aria-expanded="false" aria-controls="grouping-collapse">Click to toggle</button></label>
                <div className="collapse" id="grouping-collapse">
                    <span className="fw-lighter fst-italic mb-2 mt-0">Features that meet the notification criteria will be visually grouped in a table according to the selected NUTS level.</span>
                    <div id="grouping" className="btn-group" role="group">
                        {GROUPING_LEVELS.map((level) => <Fragment key={level}>
                            <input type="radio" className="btn-check" name="grouping" id={`roi-grouping-${level}`} autoComplete="off"
                                checked={groupingLevel === level} onChange={() => setGroupingLevel(level)} />
                            <label className="btn btn-outline-dark" htmlFor={`roi-grouping-${level}`}>NUTS Level {level}</label>
                        </Fragment>)}
                    </div>
                    <div className="mt-4">Example notification grouping that you would see based on the selected level:</div>
                    <div>
                        {groupingLevel === 0 &&
                            <table className={`table rounded border my-3 ${styles.tableWithBorder}`}>
                                <tbody>
                                    <tr><th colSpan={4}>Region</th><th colSpan={1}>Affected Population</th><th colSpan={1}>Rule</th></tr>
                                    <tr><td colSpan={4}>Spain</td><td colSpan={1}>69420</td><td colSpan={1}>AMAYA 7-48H MR</td></tr>
                                    <tr><th colSpan={6}>Notifications</th></tr>
                                    <tr><th colSpan={2}>River</th><th>Level</th><th>Forecast</th><th>Peak</th><th>ID</th></tr>
                                    <tr className="alertlevel-2" data-ogsb=""><td colSpan={2}>Turia</td><td>2</td><td>2024-10-30 18:00 UTC</td><td>2024-10-31 06:00 UTC</td><td>&lt;ID&gt;</td></tr>
                                    <tr className="alertlevel-3" data-ogsb=""><td colSpan={2}>Vinalopo</td><td>3</td><td>2024-10-30 19:00 UTC</td><td>2024-10-31 06:00 UTC</td><td>&lt;ID&gt;</td></tr>
                                    <tr className="alertlevel-4" data-ogsb=""><td colSpan={2}>Ebro</td><td>4</td><td>2024-10-30 19:00 UTC</td><td>2024-10-31 06:00 UTC</td><td>&lt;ID&gt;</td></tr>
                                    <tr className="alertlevel-2" data-ogsb=""><td colSpan={2}>Llobregat</td><td>2</td><td>2024-10-30 19:00 UTC</td><td>2024-10-31 06:00 UTC</td><td>&lt;ID&gt;</td></tr>
                                    <tr className="alertlevel-3" data-ogsb=""><td colSpan={2}>Almería</td><td>3</td><td>2024-10-30 18:00 UTC</td><td>2024-10-31 06:00 UTC</td><td>&lt;ID&gt;</td></tr>
                                </tbody>
                            </table>}
                        {groupingLevel === 1 &&
                            <table className="table rounded border my-3">
                                <tbody>
                                    <tr><th colSpan={4}>Region</th><th colSpan={1}>Affected Population</th><th colSpan={1}>Rule</th></tr>
                                    <tr><td colSpan={4}>Este</td><td colSpan={1}>69420</td><td colSpan={1}>AMAYA 7-48H MR</td></tr>
                                    <tr><th colSpan={6}>Notifications</th></tr>
                                    <tr><th colSpan={2}>River</th><th>Level</th><th>Forecast</th><th>Peak</th><th>ID</th></tr>
                                    <tr className="alertlevel-2" data-ogsb=""><td colSpan={2}>Turia</td><td>2</td><td>2024-10-30 18:00 UTC</td><td>2024-10-31 06:00 UTC</td><td>&lt;ID&gt;</td></tr>
                                    <tr className="alertlevel-3" data-ogsb=""><td colSpan={2}>Vinalopo</td><td>3</td><td>2024-10-30 19:00 UTC</td><td>2024-10-31 06:00 UTC</td><td>&lt;ID&gt;</td></tr>
                                    <tr className="alertlevel-4" data-ogsb=""><td colSpan={2}>Ebro</td><td>4</td><td>2024-10-30 19:00 UTC</td><td>2024-10-31 06:00 UTC</td><td>&lt;ID&gt;</td></tr>
                                    <tr className="alertlevel-2" data-ogsb=""><td colSpan={2}>Llobregat</td><td>2</td><td>2024-10-30 19:00 UTC</td><td>2024-10-31 06:00 UTC</td><td>&lt;ID&gt;</td></tr>
                                    <tr><td className="x_emptyrow" colSpan={6}></td></tr>
                                    <tr><th colSpan={4}>Region</th><th colSpan={1}>Affected Population</th><th colSpan={1}>Rule</th></tr>
                                    <tr><td colSpan={4}>Sur</td><td colSpan={1}>69420</td><td colSpan={1}>AMAYA 7-48H MR</td></tr>
                                    <tr><th colSpan={6}>Notifications</th></tr>
                                    <tr><th colSpan={2}>River</th><th>Level</th><th>Forecast</th><th>Peak</th><th>ID</th></tr>
                                    <tr className="alertlevel-3" data-ogsb=""><td colSpan={2}>Almería</td><td>3</td><td>2024-10-30 18:00 UTC</td><td>2024-10-31 06:00 UTC</td><td>&lt;ID&gt;</td></tr>
                                </tbody>
                            </table>}
                        {groupingLevel === 2 &&
                            <table className="table rounded border my-3">
                                <tbody>
                                    <tr><th colSpan={4}>Region</th><th colSpan={1}>Affected Population</th><th colSpan={1}>Rule</th></tr>
                                    <tr><td colSpan={4}>Comunitat Valenciana</td><td colSpan={1}>69420</td><td colSpan={1}>AMAYA 7-48H MR</td></tr>
                                    <tr><th colSpan={6}>Notifications</th></tr>
                                    <tr><th colSpan={2}>River</th><th>Level</th><th>Forecast</th><th>Peak</th><th>ID</th></tr>
                                    <tr className="alertlevel-2" data-ogsb=""><td colSpan={2}>Turia</td><td>2</td><td>2024-10-30 18:00 UTC</td><td>2024-10-31 06:00 UTC</td><td>&lt;ID&gt;</td></tr>
                                    <tr className="alertlevel-3" data-ogsb=""><td colSpan={2}>Vinalopo</td><td>3</td><td>2024-10-30 19:00 UTC</td><td>2024-10-31 06:00 UTC</td><td>&lt;ID&gt;</td></tr>
                                    <tr><td className="x_emptyrow" colSpan={6}></td></tr>
                                    <tr><th colSpan={4}>Region</th><th colSpan={1}>Affected Population</th><th colSpan={1}>Rule</th></tr>
                                    <tr><td colSpan={4}>Cataluña</td><td colSpan={1}>204540</td><td colSpan={1}>AMAYA 7-48H MR</td></tr>
                                    <tr><th colSpan={6}>Notifications</th></tr>
                                    <tr><th colSpan={2}>River</th><th>Level</th><th>Forecast</th><th>Peak</th><th>ID</th></tr>
                                    <tr className="alertlevel-4" data-ogsb=""><td colSpan={2}>Ebro</td><td>4</td><td>2024-10-30 19:00 UTC</td><td>2024-10-31 06:00 UTC</td><td>&lt;ID&gt;</td></tr>
                                    <tr className="alertlevel-2" data-ogsb=""><td colSpan={2}>Llobregat</td><td>2</td><td>2024-10-30 19:00 UTC</td><td>2024-10-31 06:00 UTC</td><td>&lt;ID&gt;</td></tr>
                                    <tr><td className="x_emptyrow" colSpan={6}></td></tr>
                                    <tr><th colSpan={4}>Region</th><th colSpan={1}>Affected Population</th><th colSpan={1}>Rule</th></tr>
                                    <tr><td colSpan={4}>Andalucía</td><td colSpan={1}>204540</td><td colSpan={1}>AMAYA 7-48H MR</td></tr>
                                    <tr><th colSpan={6}>Notifications</th></tr>
                                    <tr><th colSpan={2}>River</th><th>Level</th><th>Forecast</th><th>Peak</th><th>ID</th></tr>
                                    <tr className="alertlevel-3" data-ogsb=""><td colSpan={2}>Almanzora</td><td>3</td><td>2024-10-30 19:00 UTC</td><td>2024-10-31 06:00 UTC</td><td>&lt;ID&gt;</td></tr>
                                </tbody>
                            </table>}
                        {groupingLevel === 3 &&
                            <table className="table rounded border my-3">
                                <tbody>
                                    <tr><th colSpan={4}>Region</th><th colSpan={1}>Affected Population</th><th colSpan={1}>Rule</th></tr>
                                    <tr><td colSpan={4}>Valencia</td><td colSpan={1}>69420</td><td colSpan={1}>AMAYA 7-48H MR</td></tr>
                                    <tr><th colSpan={6}>Notifications</th></tr>
                                    <tr><th colSpan={2}>River</th><th>Level</th><th>Forecast</th><th>Peak</th><th>ID</th></tr>
                                    <tr className="alertlevel-2" data-ogsb=""><td colSpan={2}>Turia</td><td>2</td><td>2024-10-30 18:00 UTC</td><td>2024-10-31 06:00 UTC</td><td>&lt;ID&gt;</td></tr>
                                    <tr><td className="x_emptyrow" colSpan={6}></td></tr>
                                    <tr><th colSpan={4}>Region</th><th colSpan={1}>Affected Population</th><th colSpan={1}>Rule</th></tr>
                                    <tr><td colSpan={4}>Alicante</td><td colSpan={1}>18483</td><td colSpan={1}>AMAYA 7-48H MR</td></tr>
                                    <tr><th colSpan={6}>Notifications</th></tr>
                                    <tr><th colSpan={2}>River</th><th>Level</th><th>Forecast</th><th>Peak</th><th>ID</th></tr>
                                    <tr className="alertlevel-3" data-ogsb=""><td colSpan={2}>Vinalopo</td><td>3</td><td>2024-10-30 19:00 UTC</td><td>2024-10-31 06:00 UTC</td><td>&lt;ID&gt;</td></tr>
                                    <tr><td className="x_emptyrow" colSpan={6}></td></tr>
                                    <tr><th colSpan={4}>Region</th><th colSpan={1}>Affected Population</th><th colSpan={1}>Rule</th></tr>
                                    <tr><td colSpan={4}>Tarragona</td><td colSpan={1}>6565</td><td colSpan={1}>AMAYA 7-48H MR</td></tr>
                                    <tr><th colSpan={6}>Notifications</th></tr>
                                    <tr><th colSpan={2}>River</th><th>Level</th><th>Forecast</th><th>Peak</th><th>ID</th></tr>
                                    <tr className="alertlevel-4" data-ogsb=""><td colSpan={2}>Ebro</td><td>4</td><td>2024-10-30 19:00 UTC</td><td>2024-10-31 06:00 UTC</td><td>&lt;ID&gt;</td></tr>
                                    <tr><td className="x_emptyrow" colSpan={6}></td></tr>
                                    <tr><th colSpan={4}>Region</th><th colSpan={1}>Affected Population</th><th colSpan={1}>Rule</th></tr>
                                    <tr><td colSpan={4}>Barcelona</td><td colSpan={1}>12341</td><td colSpan={1}>AMAYA 7-48H MR</td></tr>
                                    <tr><th colSpan={6}>Notifications</th></tr>
                                    <tr><th colSpan={2}>River</th><th>Level</th><th>Forecast</th><th>Peak</th><th>ID</th></tr>
                                    <tr className="alertlevel-2" data-ogsb=""><td colSpan={2}>Llobregat</td><td>2</td><td>2024-10-30 19:00 UTC</td><td>2024-10-31 06:00 UTC</td><td>&lt;ID&gt;</td></tr>
                                    <tr><td className="x_emptyrow" colSpan={6}></td></tr>
                                    <tr><th colSpan={4}>Region</th><th colSpan={1}>Affected Population</th><th colSpan={1}>Rule</th></tr>
                                    <tr><td colSpan={4}>Almería</td><td colSpan={1}>1535</td><td colSpan={1}>AMAYA 7-48H MR</td></tr>
                                    <tr><th colSpan={6}>Notifications</th></tr>
                                    <tr><th colSpan={2}>River</th><th>Level</th><th>Forecast</th><th>Peak</th><th>ID</th></tr>
                                    <tr className="alertlevel-3" data-ogsb=""><td colSpan={2}>Almanzora</td><td>3</td><td>2024-10-30 19:00 UTC</td><td>2024-10-31 06:00 UTC</td><td>&lt;ID&gt;</td></tr>
                                </tbody>
                            </table>}
                    </div>
                </div>
            </div>
            <div className="d-flex flex-column my-4">
                <label className="form-label efas-heading-border-bottom mb-3">Email frequency <button className="btn btn-warning my-1 py-0" type="button" data-bs-toggle="collapse" data-bs-target="#frequency-collapse" aria-expanded="false" aria-controls="frequency-collapse">Click to toggle</button></label>
                <div className="collapse" id="frequency-collapse">
                    <span className="fw-lighter fst-italic mb-2 mt-0">After we send a notification, this is how long we wait before sending another.</span>
                    <div id="frequency" className="btn-group" role="group">
                        {FREQUENCY_OPTIONS.map((option) => <Fragment key={option.id}>
                            <input type="radio" className="btn-check" name="frequency" id={option.id} autoComplete="off"
                                checked={frequencyId === option.id} onChange={() => setFrequencyId(option.id)} />
                            <label className="btn btn-outline-dark" htmlFor={option.id}>{option.label}</label>
                        </Fragment>)}
                    </div>
                    OR
                    <div className="input-group">
                        <label className="btn btn-outline-dark" htmlFor={CUSTOM_FREQUENCY_ID}>Custom value in hours</label>
                        <input type="radio" className="btn-check" name="frequency" id={CUSTOM_FREQUENCY_ID} autoComplete="off"
                            checked={frequencyId === CUSTOM_FREQUENCY_ID} onChange={() => setFrequencyId(CUSTOM_FREQUENCY_ID)} />
                        <input type="number" className="form-control" name="custom-frequency" autoComplete="off" value={customFrequencyHours}
                            onChange={(e) => { setCustomFrequencyHours(e.target.value); setFrequencyId(CUSTOM_FREQUENCY_ID); }} />
                    </div>
                    <div className="mt-4">
                        Forecasts are produced hourly. Emails will <strong>always</strong> contain all notifications that have not been sent to you.<br />
                        Higher frequency means you will get more emails. <br />
                        Lower frequency means you may miss notifications with short leadtimes. The notification will still be contained in the email.<br />
                        Below you see what the system will do based on your frequency preference:
                    </div>

                    <table className="table table-bordered table-striped table-hover mt-3">
                        <thead>
                            <tr>
                                <th>Forecast</th>
                                <th>Notifications evaluated</th>
                                <th>Email sent to you</th>
                            </tr>
                        </thead>
                        <tbody>
                            {FREQUENCY_PREVIEW_ROWS.map((row) => <tr key={row.label}>
                                <td>{row.label}</td>
                                <td>YES</td>
                                <td>{row.offset === 0 || (frequencyHours > 0 && row.offset % frequencyHours === 0) ? "YES" : "NO"}</td>
                            </tr>)}
                        </tbody>
                    </table>
                </div>
            </div>
            <div className="d-flex gap-2">
                <Link to={isEdit ? `/inline/rules/${ruleId}` : "/inline/"} className="btn btn-warning" onClick={(e) => { if (!confirm('Discard changes and go back? Unsaved changes will be lost.')) { e.preventDefault(); } }}>Back</Link>
                <button type="button" className="btn btn-outline-success" onClick={() => saveRule()} disabled={isSaving}>
                    {isEdit ? "Save changes" : "Create rule"}
                </button>
            </div>
        </form>
    </div>
}
